/**
 * Built-in framework capabilities. The runner registers these at boot via
 * {@link bootstrapCapabilityRegistry} so the LLM always has a discovery
 * surface for what is available in the session.
 *
 * All built-ins are agent-side, `origin: { kind: 'framework' }`, and
 * synchronous queries against the registry — they have no side-effects
 * outside the registry itself.
 *
 * @category Runtime
 * @since 2.0.0
 */

import type { Capability } from "@skaile/workspaces/types";
import * as z from "zod";
import type { CapabilityRegistry } from "./capability-registry.js";
import { type DefinedCapability, defineCapability } from "./define-capability.js";

/**
 * Tokenize a free-text query and rank capabilities by case-insensitive
 * substring match count across `name`, `description`, and `promptFragment`.
 * Returns the top `limit` results, descending by score.
 */
function keywordSearch(capabilities: Capability[], query: string, limit: number): Capability[] {
  const tokens = query
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 0);
  if (tokens.length === 0) return capabilities.slice(0, limit);

  const scored = capabilities
    .map((cap) => {
      const haystack = [cap.name, cap.description, cap.promptFragment ?? ""]
        .join(" ")
        .toLowerCase();
      let score = 0;
      for (const token of tokens) {
        if (haystack.includes(token)) score += 1;
      }
      return { cap, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, limit).map((x) => x.cap);
}

/**
 * Build the three built-in discovery capabilities. They close over the
 * registry so the handlers stay in sync with mid-session register /
 * deregister events.
 *
 * @category Runtime
 * @since 2.0.0
 * @docLink packages/runner/capabilities#built-in-discovery-capabilities
 */
export function builtinCapabilities(registry: CapabilityRegistry): DefinedCapability[] {
  const listFilterSchema = z.strictObject({
    side: z.enum(["client", "agent"]).optional(),
    originKind: z
      .enum(["framework", "client", "agent", "flow", "skill", "mcp", "connector", "mount"])
      .optional(),
    scope: z.enum(["session", "turn"]).optional(),
  });
  return [
    defineCapability({
      name: "__capabilities.list",
      displayName: "List capabilities",
      description:
        "List available capabilities in this session. Optionally filter by side, origin kind, or scope.",
      side: "agent",
      origin: { kind: "framework" },
      kind: "query",
      userInvokable: false,
      input: listFilterSchema,
      handler: async (args) =>
        registry.list({
          side: args.side,
          origin: args.originKind,
          scope: args.scope,
        }),
    }),
    defineCapability({
      name: "__capabilities.search",
      displayName: "Search capabilities",
      description:
        "Keyword search across capability names, descriptions, and prompt fragments. Returns the top matches by score (substring match count).",
      side: "agent",
      origin: { kind: "framework" },
      kind: "query",
      userInvokable: false,
      input: z.strictObject({
        query: z.string(),
        limit: z.number().int().positive().optional(),
      }),
      handler: async ({ query, limit }) => keywordSearch(registry.list(), query, limit ?? 10),
    }),
    defineCapability({
      name: "__capabilities.describe",
      displayName: "Describe capability",
      description: "Return the full Capability descriptor for a single capability by name.",
      side: "agent",
      origin: { kind: "framework" },
      kind: "query",
      userInvokable: false,
      input: z.strictObject({ name: z.string() }),
      handler: async ({ name }) => {
        const cap = registry.resolve(name);
        if (!cap) return null;
        // Strip runtime-only fields by going through the wire-format `list`
        // and finding-by-name; cheaper than another method on the registry.
        const wire = registry.list().find((c) => c.name === name);
        return wire ?? null;
      },
    }),
  ];
}

/**
 * Register all framework built-ins onto a registry. Idempotent — the
 * registry's `register` is keyed by name and overwrites prior entries with
 * the same name.
 *
 * @category Runtime
 * @since 2.0.0
 * @docLink packages/runner/capabilities#built-in-discovery-capabilities
 */
export function bootstrapCapabilityRegistry(registry: CapabilityRegistry): void {
  for (const cap of builtinCapabilities(registry)) {
    registry.register(cap, "agent");
  }
}
